import { useEffect, useMemo, useState } from "react";
import { Newspaper, Plus, Loader2, ExternalLink, Sparkles, Languages, Trash2, Eye, EyeOff, Pencil, UploadCloud, Save, X, CalendarClock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inputCls = "w-full mt-1 border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-orange-500";
const emptyManual = { title: "", language: "ro", meta_description: "", excerpt: "", content: "", cover_image: "" };

function StatusBadge({ status }) {
  const draft = status === "draft";
  return (
    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 ${draft ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
      {draft ? "Draft" : "Publicat"}
    </span>
  );
}

export default function AdminNews() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // AI generator
  const [gen, setGen] = useState({ topic: "", keywords: "", base_language: "ro" });
  const [generating, setGenerating] = useState(false);
  const [quickGen, setQuickGen] = useState(false);

  // manual create
  const [manual, setManual] = useState(emptyManual);
  const [showManual, setShowManual] = useState(false);
  const [savingManual, setSavingManual] = useState(false);

  // edit modal
  const [editing, setEditing] = useState(null); // post object
  const [editForm, setEditForm] = useState(emptyManual);
  const [savingEdit, setSavingEdit] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/news").then((r) => setPosts(r.data.posts)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const { groups, standalone } = useMemo(() => {
    const g = {}; const solo = [];
    posts.forEach((p) => {
      if (p.group_id) { (g[p.group_id] = g[p.group_id] || []).push(p); }
      else solo.push(p);
    });
    const order = { ro: 0, hu: 1, en: 2 };
    Object.values(g).forEach((arr) => arr.sort((a, b) => (order[a.language] ?? 9) - (order[b.language] ?? 9)));
    return { groups: Object.entries(g), standalone: solo };
  }, [posts]);

  const generate = async () => {
    if (gen.topic.trim().length < 3) { toast.error("Adaugă un subiect (min. 3 caractere)."); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/news/generate", gen);
      toast.success("Articol generat în RO/HU/EN (Draft). Verifică și publică.");
      if (!data.unsplash_configured) toast.info("Unsplash nu e configurat — adaugă manual imaginea de copertă.");
      setGen({ topic: "", keywords: "", base_language: "ro" });
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const quickGenerate = async () => {
    setQuickGen(true);
    try {
      await api.post("/admin/news/quick-generate");
      toast.success("Articol săptămânal generat (RO/HU/EN) ca Draft. Necesită verificare umană înainte de publicare.");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setQuickGen(false); }
  };

  const publishManual = async () => {
    if (!manual.title.trim() || !manual.content.trim()) { toast.error("Titlu și conținut obligatorii."); return; }
    setSavingManual(true);
    try {
      await api.post("/admin/news", manual);
      toast.success("Articol publicat.");
      setManual(emptyManual); setShowManual(false); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSavingManual(false); }
  };

  const openEdit = (p) => { setEditing(p); setEditForm({ title: p.title, meta_description: p.meta_description, excerpt: p.excerpt, content: p.content, cover_image: p.cover_image, language: p.language }); };
  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.put(`/admin/news/${editing.id}`, {
        title: editForm.title, content: editForm.content, meta_description: editForm.meta_description,
        excerpt: editForm.excerpt, cover_image: editForm.cover_image,
      });
      toast.success("Articol salvat.");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSavingEdit(false); }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    setUploadingCover(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/listings/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditForm((f) => ({ ...f, cover_image: data.url }));
      toast.success("Copertă încărcată.");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setUploadingCover(false); }
  };

  const setStatus = async (p, publish) => {
    setBusy(p.id);
    try { await api.post(`/admin/news/${p.id}/${publish ? "publish" : "unpublish"}`); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const publishGroup = async (gid) => {
    setBusy(gid);
    try { const { data } = await api.post(`/admin/news/group/${gid}/publish`); toast.success(`${data.count} articole publicate.`); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const retranslate = async (gid, baseLang) => {
    setBusy(gid + "-tr");
    try { await api.post(`/admin/news/group/${gid}/retranslate`, { base_language: baseLang }); toast.success("Re-tradus RO/HU/EN din varianta de bază."); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Ștergi articolul „${p.title}"?`)) return;
    setBusy(p.id);
    try { await api.delete(`/admin/news/${p.id}`); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const Row = ({ p }) => (
    <div className="flex items-center gap-3 p-3 border-t border-slate-100" data-testid={`admin-news-${p.slug}`}>
      <span className="text-[10px] uppercase font-bold bg-slate-100 px-2 py-0.5 w-8 text-center">{p.language}</span>
      <StatusBadge status={p.status} />
      <div className="flex-1 min-w-0"><div className="font-semibold text-sm truncate">{p.title}</div><div className="text-xs text-slate-400 truncate">/{p.slug}</div></div>
      <button onClick={() => openEdit(p)} className="p-2 hover:bg-slate-100 text-slate-500" title="Editează" data-testid={`news-edit-${p.slug}`}><Pencil className="w-4 h-4" /></button>
      {p.status === "draft" ? (
        <button onClick={() => setStatus(p, true)} disabled={busy === p.id} className="p-2 hover:bg-emerald-50 text-emerald-600" title="Publică" data-testid={`news-publish-${p.slug}`}><Eye className="w-4 h-4" /></button>
      ) : (
        <button onClick={() => setStatus(p, false)} disabled={busy === p.id} className="p-2 hover:bg-amber-50 text-amber-600" title="Retrage (Draft)" data-testid={`news-unpublish-${p.slug}`}><EyeOff className="w-4 h-4" /></button>
      )}
      <a href={`/news/${p.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-slate-100 text-slate-500" title="Vezi"><ExternalLink className="w-4 h-4" /></a>
      <button onClick={() => remove(p)} disabled={busy === p.id} className="p-2 hover:bg-red-50 text-red-500" title="Șterge" data-testid={`news-delete-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
    </div>
  );

  return (
    <div data-testid="admin-news">
      <h2 className="font-bold text-lg mb-4 flex items-center gap-2"><Newspaper className="w-5 h-5" /> Motor SEO de știri (AI)</h2>

      {/* AI generator */}
      <div className="border-2 border-orange-200 bg-orange-50/40 p-5 grid gap-4 mb-8" data-testid="news-ai-generator">
        <div className="flex items-center gap-2 text-sm font-black text-orange-700 uppercase tracking-wide"><Sparkles className="w-4 h-4" /> Generator AI (RO · HU · EN)</div>
        <p className="text-xs text-slate-500 -mt-2">Dă un subiect → AI scrie articolul SEO și îl traduce automat în 3 limbi ca <b>Draft</b>. Publici după verificare umană. (Gemini 3.1 Pro — durează ~30-60s)</p>
        <div className="grid sm:grid-cols-[1fr_1fr_120px] gap-4">
          <div>
            <label className="text-xs uppercase tracking-wide font-bold text-slate-500">Subiect / temă *</label>
            <input value={gen.topic} onChange={(e) => setGen({ ...gen, topic: e.target.value })} placeholder="ex. Trenduri camere de uscare 2026" className={inputCls} data-testid="news-gen-topic" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide font-bold text-slate-500">Cuvinte-cheie imagine</label>
            <input value={gen.keywords} onChange={(e) => setGen({ ...gen, keywords: e.target.value })} placeholder="wood drying kiln, lumber" className={inputCls} data-testid="news-gen-keywords" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide font-bold text-slate-500">Limbă sursă</label>
            <Select value={gen.base_language} onValueChange={(v) => setGen({ ...gen, base_language: v })}>
              <SelectTrigger className="rounded-none mt-1 bg-white" data-testid="news-gen-lang"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="ro">RO</SelectItem><SelectItem value="hu">HU</SelectItem><SelectItem value="en">EN</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={generate} disabled={generating || quickGen} className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors disabled:opacity-50" data-testid="news-generate-btn">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {generating ? "Se generează..." : "Generează articol"}
          </button>
          <button onClick={quickGenerate} disabled={generating || quickGen} title="Generează un subiect din industria lemnului, ca Draft" className="inline-flex items-center gap-2 border border-orange-300 text-orange-700 hover:bg-orange-100 px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors disabled:opacity-50" data-testid="news-quick-generate-btn">
            {quickGen ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />} {quickGen ? "Se generează..." : "Generare rapidă (săptămânală)"}
          </button>
        </div>
      </div>

      {/* AI groups */}
      <h3 className="font-bold text-sm uppercase tracking-wide text-slate-500 mb-3">Articole AI ({groups.length} grupuri)</h3>
      {loading ? <div className="text-slate-400 text-sm py-6">Se încarcă...</div> : groups.length === 0 ? (
        <div className="border border-slate-200 p-6 text-center text-slate-400 text-sm mb-8">Niciun articol generat încă.</div>
      ) : (
        <div className="space-y-4 mb-10">
          {groups.map(([gid, arr]) => {
            const base = arr[0]?.base_language || "ro";
            const anyDraft = arr.some((p) => p.status === "draft");
            return (
              <div key={gid} className="border border-slate-200" data-testid={`news-group-${gid}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 p-3 bg-slate-50">
                  <div className="text-xs text-slate-500">Grup AI · <b className="text-slate-700">{arr.length} limbi</b> · bază: <b className="uppercase">{base}</b></div>
                  <div className="flex gap-2">
                    <button onClick={() => retranslate(gid, base)} disabled={busy === gid + "-tr"} className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-slate-900 px-3 py-1.5 text-xs font-bold uppercase transition-colors disabled:opacity-50" data-testid={`news-retranslate-${gid}`}>
                      {busy === gid + "-tr" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Languages className="w-3.5 h-3.5" />} Re-traduce
                    </button>
                    {anyDraft && (
                      <button onClick={() => publishGroup(gid)} disabled={busy === gid} className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 text-xs font-bold uppercase transition-colors disabled:opacity-50" data-testid={`news-publish-group-${gid}`}>
                        {busy === gid ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} Publică tot
                      </button>
                    )}
                  </div>
                </div>
                {arr.map((p) => <Row key={p.id} p={p} />)}
              </div>
            );
          })}
        </div>
      )}

      {/* Standalone / manual */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-sm uppercase tracking-wide text-slate-500">Articole individuale ({standalone.length})</h3>
        <button onClick={() => setShowManual((v) => !v)} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase text-slate-600 hover:text-slate-900" data-testid="news-toggle-manual"><Plus className="w-4 h-4" /> Manual</button>
      </div>
      {showManual && (
        <div className="border border-slate-200 p-5 grid gap-4 mb-6" data-testid="news-form">
          <div className="grid sm:grid-cols-[1fr_140px] gap-4">
            <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Titlu *</label><input value={manual.title} onChange={(e) => setManual({ ...manual, title: e.target.value })} className={inputCls} data-testid="news-title" /></div>
            <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Limbă</label>
              <Select value={manual.language} onValueChange={(v) => setManual({ ...manual, language: v })}>
                <SelectTrigger className="rounded-none mt-1 bg-white" data-testid="news-language"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ro">RO</SelectItem><SelectItem value="hu">HU</SelectItem><SelectItem value="en">EN</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Meta description</label><input value={manual.meta_description} onChange={(e) => setManual({ ...manual, meta_description: e.target.value })} maxLength={160} className={inputCls} data-testid="news-meta" /></div>
          <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Rezumat</label><input value={manual.excerpt} onChange={(e) => setManual({ ...manual, excerpt: e.target.value })} className={inputCls} data-testid="news-excerpt" /></div>
          <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Conținut (Markdown)</label><textarea value={manual.content} onChange={(e) => setManual({ ...manual, content: e.target.value })} rows={8} className={inputCls} data-testid="news-content" /></div>
          <div><button onClick={publishManual} disabled={savingManual} className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors disabled:opacity-50" data-testid="news-publish"> {savingManual ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Publică</button></div>
        </div>
      )}
      <div className="border border-slate-200">
        {standalone.length === 0 ? <div className="p-6 text-center text-slate-400 text-sm">Niciun articol individual.</div> : standalone.map((p) => <Row key={p.id} p={p} />)}
      </div>

      {/* Edit modal */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="news-edit-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-base font-black"><Pencil className="w-4 h-4" /> Editează articol <span className="text-[10px] uppercase bg-slate-100 px-2 py-0.5">{editForm.language}</span></DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Titlu</label><input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className={inputCls} data-testid="news-edit-title" /></div>
            <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Meta description</label><input value={editForm.meta_description} onChange={(e) => setEditForm({ ...editForm, meta_description: e.target.value })} maxLength={160} className={inputCls} /></div>
            <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Rezumat</label><input value={editForm.excerpt} onChange={(e) => setEditForm({ ...editForm, excerpt: e.target.value })} className={inputCls} /></div>
            <div>
              <label className="text-xs uppercase tracking-wide font-bold text-slate-500">Copertă</label>
              <div className="flex gap-2 items-center mt-1">
                <input value={editForm.cover_image} onChange={(e) => setEditForm({ ...editForm, cover_image: e.target.value })} placeholder="URL imagine sau încarcă →" className={`${inputCls} mt-0`} data-testid="news-edit-cover" />
                <label className="shrink-0 inline-flex items-center gap-1.5 border border-slate-300 hover:border-slate-900 px-3 py-2 text-xs font-bold uppercase cursor-pointer transition-colors">
                  {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} Încarcă
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadCover(e.target.files?.[0])} data-testid="news-cover-upload" />
                </label>
              </div>
              {editForm.cover_image && <img src={editForm.cover_image} alt="cover" className="mt-2 w-full h-32 object-cover border border-slate-200" />}
            </div>
            <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Conținut (Markdown)</label><textarea value={editForm.content} onChange={(e) => setEditForm({ ...editForm, content: e.target.value })} rows={12} className={inputCls} data-testid="news-edit-content" /></div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setEditing(null)} className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-900"><X className="w-4 h-4" /> Anulează</button>
            <button onClick={saveEdit} disabled={savingEdit} className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 text-sm font-bold transition-colors disabled:opacity-50" data-testid="news-edit-save">{savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvează</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
